import type { LayerV2 } from '../types';
import { anchorQuantityPerSqm, anchorPricePerPiece } from './anchors';

/**
 * Calcola il breakdown costi al m² di una stratigrafia.
 *
 * Formule per categoria:
 *  - board / ceiling_tile / finish: costo già €/m² (unit=mq) → 1 m²/m²
 *  - structure_frame (montante): ml/m² = 1000 / studSpacingMm
 *      es. passo 600mm → 1.667 ml/m² × €/ml
 *  - structure_guide (guida): ml/m² = 2 / wallHeightM ≈ 0.74 a H=2.7m
 *      (1 guida superiore + 1 inferiore per metro lineare di parete)
 *  - insulation: 1 m²/m² — i pannelli sono larghi quanto il passo, uno per vano
 *  - screw: già in layer.screwCostPerSqm (calcolato altrove)
 *
 * Sul layer guida si conteggiano anche i TASSELLI di fissaggio: 2 per ml di
 * guida (passo 50 cm), quindi `guide_ml × 2` = 1,48 pz/m² a H=2,7m. Vedi `anchors.ts`.
 *
 * Manodopera: somma `installation_time_per_sqm × cost_per_hour` per ogni layer.
 *
 * Risparmio: (list_price - net_price) × quantity, sommato per layer con sconto attivo.
 */

export interface CostRowMaterial {
  layerIdx: number;
  layerNum: number; // 1-indexed
  name: string;
  category: string;
  quantity: number;
  unit: string;
  unitPriceNet: number;
  unitPriceList: number;
  rowCost: number;
  rowSavings: number;
  /** Minuti di posa per la quantità di questa riga (m² di parete). */
  rowMinutes: number;
  notes?: string;
}

export interface CostRowScrews {
  layerIdx: number;
  layerNum: number;
  screwName: string;
  screwQuantityPerSqm: number;
  pricePerPiece: number;
  rowCost: number;
  rowMinutes: number;
}

/** Tasselli di fissaggio di un layer guida. */
export interface CostRowAnchors {
  layerIdx: number;
  layerNum: number;
  anchorName: string;
  /** Fornitore, per capire da chi si compra senza riaprire il selettore. */
  anchorSupplier: string;
  /** Pezzi per m² di parete — derivati dai ml di guida, non impostati a mano. */
  anchorQuantityPerSqm: number;
  pricePerPiece: number;
  rowCost: number;
  rowMinutes: number;
}

export interface CostBreakdown {
  rows: CostRowMaterial[];
  /** Righe viti — una per ogni layer che ha screwMaterial assegnato. */
  screwRows: CostRowScrews[];
  /** Righe tasselli — una per ogni layer guida che ha anchorMaterial assegnato. */
  anchorRows: CostRowAnchors[];
  subtotalMaterials: number;
  /** Totale viti (somma layer.screwCostPerSqm). */
  subtotalScrews: number;
  /** Totale tasselli di fissaggio guide. */
  subtotalAnchors: number;
  /** Tempo manodopera totale in MINUTI per m² (somma minuti righe). */
  laborMinutes: number;
  /** Tempo manodopera in h/m² (laborMinutes / 60). */
  laborTime: number;
  /** Costo manodopera = laborTime × costPerHour. */
  laborCost: number;
  /** Totale generale €/m² (materiali + viti + manodopera). */
  totalCost: number;
  /** Risparmio totale applicato (delta listino - netto). */
  totalSavings: number;
}

/**
 * Altezza convenzionale per il calcolo delle guide (m).
 *
 * NB: e' una convenzione, non l'altezza vera della parete — quella esiste solo
 * sulla riga di preventivo (`estimate_stratigraphies.wall_height`) e nessuno
 * dei chiamanti di questa funzione ce l'ha. Su una parete da 3 m il €/m² qui
 * calcolato e il Riepilogo Materiali (che l'altezza vera la usa) divergono di
 * circa il 10%.
 */
export const WALL_HEIGHT_M = 2.7;

/**
 * Metri lineari di guida per m² di parete: una a pavimento e una a soffitto,
 * spalmate sull'altezza. A H = 2,7 m sono 0,74 ml/m².
 *
 * Esportata perche' i TASSELLI si contano su questa (2 pz per ml di guida):
 * legandoli qui restano coerenti con le guide anche se la formula cambia.
 */
export function guideQuantityPerSqm(wallHeightM: number = WALL_HEIGHT_M): number {
  const h = Number(wallHeightM) > 0 ? Number(wallHeightM) : WALL_HEIGHT_M;
  return 2 / h;
}

export function computeStratigraphyCosts(
  layers: LayerV2[],
  studSpacingMm: number,
  costPerHour: number,
): CostBreakdown {
  const rows: CostRowMaterial[] = [];
  const screwRows: CostRowScrews[] = [];
  const anchorRows: CostRowAnchors[] = [];
  let subtotalMaterials = 0;
  let subtotalScrews = 0;
  let subtotalAnchors = 0;
  let laborMinutes = 0;
  let totalSavings = 0;

  layers.forEach((l, idx) => {
    const m = l.material;
    if (!m || !l.materialId) return;

    const cat = m.category;
    // Prezzo della CONFEZIONE, non dell'unità: i listini quotano il sacco.
    // "GYPSOMAF - 10 kg" costa 4,148 € netti *a sacco*, e il listino Fassa lo
    // scrive in una colonna intestata "€/kg" — che indica il contenuto, non
    // l'unità di prezzo. La prova sta nella riga accanto: lo stesso prodotto da
    // 25 kg costa 8,516, cioè il doppio, non due volte e mezzo.
    //
    // Senza dividere, 2 kg/m² di colla venivano contati 8,30 €/m² invece di
    // 0,83: dieci volte tanto. Il campo `box_pieces` esiste da sempre e il form
    // lo mostra anche per le finiture ("EvoPlus 10 kg → box_pieces=10"), ma qui
    // veniva letto solo per viti e tasselli.
    const box = Number(m.box_pieces ?? 0);
    const perUnit = (p: number) => (box > 0 ? p / box : p);
    const netPrice = perUnit(Number(m.net_price ?? m.unit_price ?? 0));
    const listPrice = perUnit(Number(m.list_price ?? Number(m.net_price ?? m.unit_price ?? 0)));
    const unit = m.unit ?? '—';

    let quantity = 0;
    let notes: string | undefined;
    /**
     * Quantita` con cui si moltiplica il TEMPO di posa, quando e` diversa da
     * quella con cui si moltiplica il costo. Vedi il ramo delle lastre.
     */
    let timeQuantity: number | null = null;

    if (cat === 'board' || cat === 'ceiling_tile' || cat === 'finish') {
      // Di norma il prezzo è già al m² → 1 m²/m².
      //
      // Ma non sempre: una malta adesiva da placcaggio (GYPSOMAF) è venduta a
      // KG e si consuma 2 kg/m². Con `quantity = 1` fisso il suo prezzo al kg
      // veniva contato come se fosse un prezzo al m²: 4,15 €/m² invece di 8,30,
      // metà del dovuto. Nel frattempo la RDA ordinava `area × incidenza`
      // (exportCompleteRDA), cioè compravi 2 kg e ne fatturavi 1.
      //
      // L'incidenza è già sul materiale e vale 1 per tutte le lastre: leggerla
      // qui allinea costo e RDA senza muovere nulla di quello che funziona.
      //
      // Il layer può però dichiarare un consumo suo (`materialQuantity`), che
      // vince: lo stesso prodotto ha consumi diversi secondo l'uso — l'A 96
      // della Fassa si usa sia per rasare sia per incollare.
      const dichiarato = Number(l.materialQuantity ?? NaN);
      const inc = Number.isFinite(dichiarato) && dichiarato > 0
        ? dichiarato
        : Number(m.incidence_per_sqm ?? 1);
      quantity = Number.isFinite(inc) && inc > 0 ? inc : 1;
      if (quantity !== 1) notes = `${quantity.toFixed(2)} ${unit}/m²`;
      // Il TEMPO resta al m² di parete, non al kg di prodotto: incollare un
      // metro quadro di lastra dura quello che dura, che di malta se ne
      // consumino 2 kg o 2,5. Moltiplicarlo per l'incidenza raddoppierebbe in
      // silenzio i 5 min/m² impostati sulla scheda, portandoli a 10.
      timeQuantity = 1;
    } else if (cat === 'structure_frame') {
      // €/ml → ml/m² = 1000/passo
      quantity = 1000 / studSpacingMm;
      notes = `${quantity.toFixed(2)} ml/m² (passo ${studSpacingMm}mm)`;
    } else if (cat === 'structure_guide') {
      // €/ml → 2 guide per ml di parete, divise per altezza
      quantity = guideQuantityPerSqm();
      notes = `${quantity.toFixed(2)} ml/m² (H=${WALL_HEIGHT_M}m)`;
    } else if (cat === 'insulation') {
      // 1 m² di pannello per m² di parete.
      //
      // Prima si contava l'area geometrica del vano ((passo-montante)/passo,
      // es. 84% a passo 600 con montante da 99). E' la superficie che la lana
      // occupa una volta installata, ma NON e' quanta ne consumi: i pannelli
      // sono larghi quanto il passo (600 per interasse 600) e se ne mette uno
      // per vano, a pressione. Il numero di pannelli e' lo stesso a prescindere
      // dalla larghezza del montante: cambia solo quanto il pannello si comprime.
      //
      // Il difetto si vedeva a occhio: la parete con l'orditura da 75 risultava
      // consumare piu' lana (88%) di quella da 100 (84%), e siccome il tempo di
      // posa segue la quantita', posare una struttura da 100 risultava piu'
      // veloce che posarne una da 75.
      quantity = 1;
      notes = '1,00 m²/m² — un pannello per vano';
    } else if (cat === 'screw') {
      // Le viti come materiale-layer sono ignorate qui (gestite via layer.screwMaterial)
      return;
    } else {
      // accessory, other → costo diretto
      quantity = 1;
    }

    const rowCost = netPrice * quantity;
    const rowSavings = (listPrice - netPrice) * quantity;
    // Tempo manodopera: installation_time_per_sqm è in ORE per unit del materiale.
    // Convertiamo in minuti totali per la quantità di m² di parete.
    const timePerUnitH = Number(m.installation_time_per_sqm ?? 0);
    const rowMinutes = timePerUnitH * (timeQuantity ?? quantity) * 60;

    rows.push({
      layerIdx: idx,
      layerNum: idx + 1,
      name: m.name ?? '—',
      category: cat,
      quantity,
      unit,
      unitPriceNet: netPrice,
      unitPriceList: listPrice,
      rowCost,
      rowSavings,
      rowMinutes,
      notes,
    });
    subtotalMaterials += rowCost;
    if (rowSavings > 0.001) totalSavings += rowSavings;
    if (rowMinutes > 0) laborMinutes += rowMinutes;

    // === VITI del layer (se presenti) ===
    // screwQuantity è il numero di viti per m² di parete.
    // installation_time_per_sqm sulla vite è ORE per singola vite (es. 5s = 5/3600 h/pz).
    const screwQty = Number(l.screwQuantity ?? 0);
    const screw = l.screwMaterial;
    // Il costo viti si RICALCOLA dal prezzo corrente della vite, non si legge
    // da `layer.screwCostPerSqm`: quel campo e' congelato al momento in cui la
    // parete e' stata composta e non segue ne' i listini ne' gli sconti dell'org.
    // Su PV017 conteneva 0,12664 €/pz mentre il netto Drycore e' 0,054 €/pz
    // (13,50 € la scatola da 250) → 866 € di viti sovrastimate su 702 m², e il
    // preventivo non tornava col Riepilogo Materiali, che il prezzo lo rilegge.
    // Il valore salvato resta come fallback per i layer senza materiale vite.
    const pricePerPiece = (() => {
      if (!screw) return 0;
      const unitPrice = Number(screw.net_price ?? screw.unit_price ?? 0);
      if (!(unitPrice > 0)) return 0;
      const screwUnit = String(screw.unit ?? '').toLowerCase().trim();
      const box = Number(screw.box_pieces ?? 0);
      // Le viti si vendono a scatola: il prezzo unitario e' della SCATOLA.
      return (screwUnit === 'scatola' && box > 0) ? unitPrice / box : unitPrice;
    })();
    const screwCostPerSqm = pricePerPiece > 0 && screwQty > 0
      ? pricePerPiece * screwQty
      : Number(l.screwCostPerSqm ?? 0);
    if (screwCostPerSqm > 0 && screw && screwQty > 0) {
      const screwTimePerPieceH = Number(screw.installation_time_per_sqm ?? 0);
      const screwMinutes = screwTimePerPieceH * screwQty * 60;
      screwRows.push({
        layerIdx: idx,
        layerNum: idx + 1,
        screwName: screw.name ?? 'Vite',
        screwQuantityPerSqm: screwQty,
        pricePerPiece,
        rowCost: screwCostPerSqm,
        rowMinutes: screwMinutes,
      });
      subtotalScrews += screwCostPerSqm;
      laborMinutes += screwMinutes;
    } else if (screwCostPerSqm > 0) {
      // Fallback: cost senza materiale vite associato
      subtotalScrews += screwCostPerSqm;
    }

    // === TASSELLI del layer guida (se presenti) ===
    // La quantita' NON e' un numero salvato: si deriva dai ml di guida appena
    // calcolati (`quantity`), 2 tasselli per ml a passo 50 cm. Cosi' i tasselli
    // restano coerenti con le guide per costruzione, e per metro lineare di
    // parete sono sempre 4 a qualunque altezza.
    if (cat === 'structure_guide' && l.anchorMaterial) {
      const anchorQty = anchorQuantityPerSqm(quantity);
      // Se il prezzo al pezzo non e' ricavabile (venduto a confezione senza
      // sapere quanti pezzi contiene) NON si indovina: si conta zero. Leggere
      // il prezzo della scatola come prezzo del pezzo darebbe ~15 €/m² di soli
      // tasselli — e' il bug PV017 delle viti, che non va ripetuto.
      const anchorPrice = anchorPricePerPiece(l.anchorMaterial);
      if (anchorPrice != null && anchorQty > 0) {
        const anchorTimePerPieceH = Number(l.anchorMaterial.installation_time_per_sqm ?? 0);
        const anchorMinutes = anchorTimePerPieceH * anchorQty * 60;
        anchorRows.push({
          layerIdx: idx,
          layerNum: idx + 1,
          anchorName: l.anchorMaterial.name ?? 'Fissaggio',
          anchorSupplier: l.anchorMaterial.supplier ?? '',
          anchorQuantityPerSqm: anchorQty,
          pricePerPiece: anchorPrice,
          rowCost: anchorPrice * anchorQty,
          rowMinutes: anchorMinutes,
        });
        subtotalAnchors += anchorPrice * anchorQty;
        laborMinutes += anchorMinutes;
      }
    }
  });

  const laborTime = laborMinutes / 60;
  const laborCost = laborTime * (costPerHour > 0 ? costPerHour : 30);
  const totalCost = subtotalMaterials + subtotalScrews + subtotalAnchors + laborCost;

  return {
    rows,
    screwRows,
    anchorRows,
    subtotalMaterials,
    subtotalScrews,
    subtotalAnchors,
    laborMinutes,
    laborTime,
    laborCost,
    totalCost,
    totalSavings,
  };
}
